import { Grid } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ICandidate,
  ICandidateSubmissionSearchFilterVM,
  getPendingSubmissionCount,
} from 'wisestep-api';
import { WsColors } from 'wisestep-base';

import getDecodedQueryParams from '../../../utils/getDecodedQueryParams';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import {
  ISubmissionAdvanceFilters,
  defaultSubmissionsListingSearchQuery,
  getDefaultFilterValues,
  processUserInputsToFilters,
} from '../utils/Helper';

import AdvancedFilters from './filters/advanced/AdvancedFilters';
import Filters from './filters/Filters';
import ApplyedFilter from './filters/smart/ApplyedFilter';
import Header from './header';
import useSubmissionsNewContext from './hooks/useSubmissionsNewContext';
import Table from './Table/Table';

const Layout = () => {
  const [params, setParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState<ICandidateSubmissionSearchFilterVM>(
    params.get('search')
      ? getDecodedQueryParams(params.get('search') ?? '')
      : {
          ...defaultSubmissionsListingSearchQuery,
          sort: ['createdOn,desc'],
        }
  );
  const { submissions, refetch, setSubmissions } = useSubmissionsNewContext(searchQuery);
  const [openAdvancedFilters, setOpenAdvancedFilters] = useState<boolean>(false);
  const [selectedC, setSelectedC] = useState<ICandidate[]>();
  const [unselectAllRow, setUnselectAllRow] = useState<boolean>(false);
  const [selectRows, setSelectRows] = useState<boolean>(false);
  const [updatedRow, setUpdatedRow] = useState<ICandidate[]>();
  const requiredFilterNames = ['JOB_STATUS', 'ACCOUNT_MANAGER'];

  const [advancedFilterDefaults, setAdvancedFilterDefaults] = useState<ISubmissionAdvanceFilters>(
    getDefaultFilterValues(searchQuery)
  );
  const [pendingCount, setPendingCount] = useState<number>(0);
  const onDeleteFilterTag = (data) => {
    setOpenAdvancedFilters(false);

    const queryUpdate =
      data.split(' : ')[0] === 'SubmittedOrUpdatedbetween'
        ? 'SUBMITTED_OR_UPDATED_BETWEEN'
        : data.split(' : ')[0] === 'APPLICATION_STAGE' ||
          data.split(' : ')[0] === 'APPLICATION_STATUS'
        ? 'STAGE_STATUS'
        : data.split(' : ')[0];
    const keyToRemove = data.split(' : ')[0] === 'SALARY' ? 'salaryRange' : data.split(' : ')[0];
    const updatedAdvancedFilters = Object.keys(advancedFilterDefaults).reduce((result, key) => {
      if (key === keyToRemove) {
        if (keyToRemove === 'salaryRange') {
          result[key] = null;
        } else if (keyToRemove === 'SubmittedOrUpdatedbetween') {
          result[key] = '';
          result['SubmittedOrUpdatedFromDate'] = '';
          result['SubmittedOrUpdatedToDate'] = '';
        }
      } else if (
        !(
          (key === 'SubmittedOrUpdatedFromDate' || key === 'SubmittedOrUpdatedToDate') &&
          keyToRemove === 'SubmittedOrUpdatedbetween'
        ) &&
        !(
          (key === 'stageDateFromDate' || key === 'stageDateToDate') &&
          keyToRemove === 'STAGE_STATUS_BETWEEN'
        ) &&
        !(
          key === 'STAGE_STATUS' &&
          (keyToRemove === 'APPLICATION_STAGE' || keyToRemove === 'APPLICATION_STATUS')
        )
      ) {
        result[key] = advancedFilterDefaults[key];
      }
      return result;
    }, {} as ISubmissionAdvanceFilters);
    setAdvancedFilterDefaults(updatedAdvancedFilters);
    const updatingFilters = searchQuery.filters.filter(
      (filters) => filters.filterName !== queryUpdate
    );
    setSearchQuery({ ...searchQuery, filters: updatingFilters });
    setParams({
      search: getEncodedQueryParams(JSON.stringify({ ...searchQuery, filters: updatingFilters })),
    });
  };

  const fetchPendingCount = async () => {
    try {
      const response = await getPendingSubmissionCount();
      setPendingCount(response.data);
    } catch (error) {
      console.error('Error fetching pending count:', error);
    }
  };

  useEffect(() => {
    fetchPendingCount();
  }, []);

  const onFormSubmit = (data: ISubmissionAdvanceFilters) => {
    setOpenAdvancedFilters(false);
    const processedFilters = isEmpty(data) ? [] : processUserInputsToFilters(data);
    const filtersExisted = searchQuery.filters.filter((filter) =>
      requiredFilterNames.includes(filter.filterName)
    );
    const updatedQuery = isEmpty(data)
      ? { ...defaultSubmissionsListingSearchQuery }
      : {
          ...searchQuery,
          filters: [...filtersExisted, ...processedFilters],
          page: 0,
          jobs: data.JOB_ID,
        };

    setSearchQuery(updatedQuery);
    setAdvancedFilterDefaults(isEmpty(data) ? ({} as ISubmissionAdvanceFilters) : data);
    setParams({ search: getEncodedQueryParams(JSON.stringify(updatedQuery)) });
  };
  const onClear = () => {
    const query = { ...defaultSubmissionsListingSearchQuery };
    query.query = '';
    setSearchQuery(query);
    setAdvancedFilterDefaults({} as ISubmissionAdvanceFilters);
    setParams({ search: getEncodedQueryParams(JSON.stringify(query)) });
  };
  const updateSubmissionData = (updatedSubmissionContent: ICandidate[]) =>
    setSubmissions({ ...submissions, content: updatedSubmissionContent });
  const updateSearchQuery = (updatedSearchQuery: ICandidateSubmissionSearchFilterVM) => {
    setSearchQuery(updatedSearchQuery);
    setParams({ search: getEncodedQueryParams(JSON.stringify(updatedSearchQuery)) });
  };
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Header
          searchQuery={searchQuery}
          getUpdateFiltersFromHeaders={(query) => {
            setSearchQuery(query);
            setParams({ search: getEncodedQueryParams(JSON.stringify(query)) });
          }}
          pendingCount={pendingCount}
        />
      </Grid>
      <Grid item xs={openAdvancedFilters ? 9 : 12} pb={6}>
        <Filters
          onAdvancedFiltersClick={() => setOpenAdvancedFilters(!openAdvancedFilters)}
          searchQuery={searchQuery}
          getUpdatedFiltersFromFilters={(query) => {
            setSearchQuery(query);
            setParams({ search: getEncodedQueryParams(JSON.stringify(query)) });
          }}
          selectedCandidates={selectedC ?? []}
          updateUI={(value) => setUpdatedRow(value)}
          setOpenAdvancedFilters={setOpenAdvancedFilters}
          clear={onClear}
        />
        <Grid
          container
          item
          xs={12}
          px={2}
          bgcolor={WsColors.White}
          pb={1}
          sx={{ border: '1px  solid #E0E0E0', borderTop: 'none' }}
        >
          <ApplyedFilter
            searchQuery={searchQuery ?? []}
            onDelete={(data) => onDeleteFilterTag(data)}
            getUpdatedFiltersFromAppliedFilters={(query) => {
              setSearchQuery(query);
              setParams({ search: getEncodedQueryParams(JSON.stringify(query)) });
            }}
          />
        </Grid>
        <Table
          searchQuery={searchQuery}
          getUpdatedFiltersFromTable={(query) => {
            setSearchQuery(query);
            setParams({ search: getEncodedQueryParams(JSON.stringify(query)) });
          }}
          selectedCandidate={(value) => {
            value && value.length === 0
              ? setUnselectAllRow(false)
              : setUnselectAllRow(unselectAllRow);
            value && value.length === 15 && setSelectRows(false);
            setSelectedC(value);
          }}
          submissionsData={submissions}
          unSelect={unselectAllRow}
          selectRows={selectRows}
          updateRow={updatedRow}
          refreshPendingCount={fetchPendingCount}
          refetch={refetch}
          updateSubmissionData={updateSubmissionData}
          updateSearchQuery={updateSearchQuery}
        />
      </Grid>
      {openAdvancedFilters && (
        <Grid item xs={3}>
          <AdvancedFilters
            onClose={() => setOpenAdvancedFilters(false)}
            defaultValues={advancedFilterDefaults}
            onSubmit={onFormSubmit}
          />
        </Grid>
      )}
    </Grid>
  );
};
export default Layout;
